import React from 'react';
import { Coins, TrendingUp, ShoppingBag, Trophy, Menu, X, Shield, Wallet } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeaderProps {
  onOpenStats: () => void;
  onOpenShop: () => void;
  onOpenAchievements: () => void;
  onOpenFortuneWheel: () => void;
  onOpenAdmin?: () => void;
  onOpenWallet?: () => void;
  walletAddress?: string | null;
  isAdmin?: boolean;
}

export function Header({
  onOpenStats,
  onOpenShop,
  onOpenAchievements,
  onOpenFortuneWheel,
  onOpenAdmin,
  onOpenWallet,
  walletAddress,
  isAdmin = false,
}: HeaderProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  return (
    <>
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Coins className="text-yellow-500" />
              Crypto Clicker
            </h1>
            
            <div className="flex items-center gap-4">
              {walletAddress && (
                <button
                  onClick={onOpenWallet}
                  className="hidden md:flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Wallet className="text-gray-400" size={20} />
                  <span className="text-gray-300 font-mono text-sm">
                    {walletAddress}
                  </span>
                </button>
              )}
              
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors"
              >
                <Menu size={20} />
                <span>Menu</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed inset-y-0 right-0 w-80 bg-gray-900 shadow-2xl z-[9999] border-l border-gray-700"
            >
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-gray-700">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Menu</h2>
                    <button
                      onClick={() => setIsSidebarOpen(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  
                  {walletAddress && (
                    <button
                      onClick={() => { setIsSidebarOpen(false); onOpenWallet?.(); }}
                      className="mt-4 w-full p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Wallet className="text-gray-400" size={20} />
                        <span className="text-gray-300 font-mono text-sm break-all">
                          {walletAddress}
                        </span>
                      </div>
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    <button
                      onClick={() => { setIsSidebarOpen(false); onOpenFortuneWheel(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                    >
                      <Coins size={20} />
                      Fortune Wheel
                    </button>

                    <button
                      onClick={() => { setIsSidebarOpen(false); onOpenShop(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                    >
                      <ShoppingBag size={20} />
                      Shop
                    </button>

                    <button
                      onClick={() => { setIsSidebarOpen(false); onOpenStats(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    >
                      <TrendingUp size={20} />
                      Statistics
                    </button>

                    <button
                      onClick={() => { setIsSidebarOpen(false); onOpenAchievements(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-white transition-colors"
                    >
                      <Trophy size={20} />
                      Achievements
                    </button>

                    {isAdmin && onOpenAdmin && (
                      <button
                        onClick={() => { setIsSidebarOpen(false); onOpenAdmin(); }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                      >
                        <Shield size={20} />
                        Admin Panel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}